import type { LDClient } from 'launchdarkly-react-client-sdk';

import { Logger } from '@/utils/logging';

import type { TrackingInterface } from '../_types';

// The client is injected rather than imported from @/providers/LaunchDarklyProvider:
// that provider imports @/utils/tracking, so importing it back here would be a
// circular import.
let client: LDClient | null = null;

export const setLaunchDarklyClient = (ldClient: LDClient | null) => {
  client = ldClient;
};

// Bounds an explicit flush (see `flush` below) so a slow or broken network
// never blocks a caller that's about to navigate away.
const FLUSH_TIMEOUT_MS = 1500;

// A bare `setTimeout` promise raced via `Promise.race` keeps its timer alive
// even after the other side of the race wins, so the timer must be cleared
// explicitly once either side settles.
const timeoutSignal = (ms: number) => {
  let timer: ReturnType<typeof setTimeout>;
  const promise = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return { promise, cancel: () => clearTimeout(timer) };
};

export const launchDarklyTracker = (): TrackingInterface => ({
  // LD identity is owned by LaunchDarklyProvider / useIdentifyCustomer. Calling
  // identify() from here would re-key the context and break the guarantee that
  // every event fires on the key that evaluated the flag.
  setUserId: () => {},
  track: (message, context, options) => {
    client?.track(message, context, options?.metricValue);
  },
  // The SDK batches track() calls and flushes them on its own timer (plus a
  // best-effort pagehide/visibilitychange guard) — not soon enough for a
  // caller that's about to do a full external-page navigation right after
  // tracking (e.g. the BNPL provider redirect). Callers on that path await
  // this explicitly instead of relying on the unload race.
  flush: async () => {
    if (!client) return;
    const { promise, cancel } = timeoutSignal(FLUSH_TIMEOUT_MS);
    try {
      await Promise.race([client.flush(), promise]);
    } catch (error) {
      Logger.error('[TRACKING] LaunchDarkly flush failed', { error });
    } finally {
      cancel();
    }
  },
});
